import { ArrowRightIcon } from 'lucide-react';

import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import type { GroupWithDashboard } from '@splitfiesta/shared';
import { Card } from '@splitfiesta/ui';

import { MemberBalanceList } from '@/components/members/MemberBalanceList';
import { LinkButton } from '@/components/ui/LinkButton';

interface MembersSectionProps {
  group: GroupWithDashboard;
  currentUserId?: string;
}

export function MembersSection({ group, currentUserId }: MembersSectionProps) {
  const { t } = useTranslation();

  // Load members namespace for translations
  useNamespace('members');

  const members = (() => {
    const balancesData = group.balances || [];
    const allMembers = balancesData.map(balanceItem => {
      const memberId = balanceItem.userId;
      const memberName = balanceItem.userName || `Member ${memberId.slice(-4)}`;

      return {
        id: memberId,
        name: memberName,
        email: '',
        joinedAt: '',
        profileImage: balanceItem.profileImage,
        role: balanceItem.role,
        isPlaceholder: balanceItem.isPlaceholder,
        isSupporter: !!balanceItem.isSupporter,
        balance: {
          amount: Math.abs(balanceItem.balance || 0),
          currency: balanceItem.currency || group?.currency || 'USD',
          isPositive: (balanceItem.balance || 0) >= 0,
        },
        isOwner: memberId === group.createdBy,
        isCurrentUser: memberId === currentUserId,
      };
    });

    const sortedMembers = [...allMembers].sort((a, b) =>
      a.name.localeCompare(b.name)
    );

    // Always show all members with owner first
    const owner = sortedMembers.find(m => m.isOwner);
    const otherMembers = sortedMembers.filter(m => !m.isOwner);
    return owner ? [owner, ...otherMembers] : sortedMembers;
  })();

  return (
    <Card data-testid="group-balance-summary" className="p-6" padding="none">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {t('groups:sections.membersList', {
            count: group.balances?.length || group.memberCount || 0,
          })}
        </h3>
        <LinkButton href={`/groups/${group.id}/members`}>
          {t('common:actions.manage')}
          <ArrowRightIcon className="w-4 h-4 ml-1" />
        </LinkButton>
      </div>
      <MemberBalanceList
        members={members}
        currentUserId={currentUserId}
        groupId={group.id}
      />
    </Card>
  );
}
